import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { type CardSeverity, toCardSeverity } from "@entities/cardSeverity";

export interface Task {
  id: string;
  title: string;
  /**
   * Plain text, capped at 1200 chars, newlines significant. Required on new
   * `human` cards; null on older tasks, a2ui kinds (detail in `payload`) and
   * `phone_message` (`payload.message`).
   */
  details: string | null;
  kind: string | null;
  payload: unknown | null;
  status: string;
  assigneeUserId: string | null;
  assignedRoleId: string | null;
  dueAt: string | null;
  /** The agent's status signal; `reason` is the one-line why shown for watch/urgent. */
  severity: CardSeverity;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** No a2ui payload: structured fields from brian's `take_message` (modules/agent/tools/phone.py). */
export const PHONE_MESSAGE = "phone_message";

export const toTask = (t: components["schemas"]["TaskOut"]): Task => ({
  id: t.uuid,
  title: t.title,
  details: t.details ?? null,
  kind: t.kind,
  payload: t.payload,
  status: t.status,
  assigneeUserId: t.assignee_user_uuid,
  assignedRoleId: t.assigned_role_uuid,
  dueAt: t.due_at,
  severity: toCardSeverity(t.severity),
  reason: t.reason ?? null,
  createdAt: t.created_at,
  updatedAt: t.updated_at,
});

/** Keyed by the surfaceable kinds only, with no `"none"` bucket. */
type TaskKindCounts = Record<
  string,
  { open: number; urgent: number; snoozed: number; rejected: number }
>;

/** `open`/`urgent` exclude snoozed and caller-rejected tasks. */
export interface TaskCounts {
  byKind: TaskKindCounts;
}

/** `me` (backend default): the caller's user/role tasks plus unassigned; `all` is honored only for leadership. */
type AssignedTo = "me" | "all";

const OPEN_TASKS_PAGE_SIZE = 50;

/** `nextCursor` is opaque and valid only for the `order` that minted it: echo it back, never build or parse one. */
interface TaskPage {
  tasks: Task[];
  nextCursor: string | null;
}

const toTaskPage = (page: components["schemas"]["TaskPage"]): TaskPage => ({
  tasks: page.items.map(toTask),
  nextCursor: page.next_cursor ?? null,
});

export const fetchOpenTasks = (cursor?: string): Promise<TaskPage> =>
  request(() =>
    apiClient.GET("/tasks", {
      params: {
        query: {
          status: "open",
          limit: OPEN_TASKS_PAGE_SIZE,
          cursor,
          assigned_to: "me",
          // The backend default: urgent, soonest-due, oldest-created.
          order: "priority",
        },
      },
    }),
  ).then(toTaskPage);

/** The to-dos filed from one call (brian `GET /phone-calls/{uuid}/todos`) that the viewer can see. */
export const fetchCallTodos = (callUuid: string): Promise<Task[]> =>
  request(() =>
    apiClient.GET("/phone-calls/{call_uuid}/todos", {
      params: { path: { call_uuid: callUuid } },
    }),
  ).then((rows) => rows.map(toTask));

export const fetchTaskCounts = (assignedTo?: AssignedTo): Promise<TaskCounts> =>
  request(() =>
    apiClient.GET("/tasks/counts", {
      params: { query: { assigned_to: assignedTo } },
    }),
  ).then((c) => ({ byKind: c.by_kind }));

/** Every staff task action. */
export const submitTaskAction = (
  id: string,
  action: string,
  data: Record<string, unknown>,
): Promise<Task> =>
  request(() =>
    apiClient.PATCH("/tasks/{task_uuid}", {
      params: { path: { task_uuid: id } },
      body: { action, data },
    }),
  ).then(toTask);
